'use client';

import React, { useMemo, useCallback } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  flexRender,
} from '@tanstack/react-table';
import type {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  RowSelectionState,
  ExpandedState,
  GroupingState,
  Table as TanstackTable,
  Row,
  Column,
} from '@tanstack/table-core';
import { rankItem } from '@tanstack/match-sorter-utils';
import {
  LuChevronLeft,
  LuChevronRight,
  LuChevronUp,
  LuChevronDown,
  LuArrowUpDown,
  LuDownload,
  LuCopy,
  LuTrash2,
  LuEyeOff,
  LuColumns2,
  LuGripVertical,
} from 'react-icons/lu';

import { memoizeComponent, createSelectivePropsComparison } from '../utils/performance';
import { useOptimizedCallback, useDebouncedValue } from '../hooks/use-performance';

import {
  TableRoot,
  TableHeader,
  TableBody,
  TableRow as ChakraTableRow,
  TableColumnHeader,
  TableCell,
} from './table';
import { Box } from './box';
import { Button } from './button';
import { Input } from './input';
import { HStack, VStack, Stack } from './stack';
import { Text } from './text';
import { IconButton } from './icon-button';
import { Badge } from './badge';
import { Popover } from './popover';
import { Checkbox } from './checkbox';
import { DataTableSkeleton } from './data-table-skeleton';

// Types (same as original)
export interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];

  // Core features
  searchPlaceholder?: string;
  isLoading?: boolean;
  pageSize?: number;
  pageSizeOptions?: number[];

  // Feature flags
  enableRowSelection?: boolean;
  enableMultiSelect?: boolean;
  enableSorting?: boolean;
  enableFiltering?: boolean;
  enableGlobalFilter?: boolean;
  enableRowExpansion?: boolean;
  enableGrouping?: boolean;
  enableColumnVisibility?: boolean;
  enableExport?: boolean;
  enableStickyHeader?: boolean;
  enablePagination?: boolean;

  // Styling
  striped?: boolean;
  highlightOnHover?: boolean;
  variant?: 'simple' | 'striped' | 'unstyled';
  size?: 'sm' | 'md' | 'lg';
  colorPalette?: string;

  // Callbacks
  onRowSelectionChange?: (rowSelection: RowSelectionState) => void;
  onRowClick?: (row: Row<TData>) => void;
  onRowDoubleClick?: (row: Row<TData>) => void;
  onExport?: (data: TData[]) => void;

  // Custom components
  renderTopToolbar?: (table: TanstackTable<TData>) => React.ReactNode;
  renderBottomToolbar?: (table: TanstackTable<TData>) => React.ReactNode;
  renderRowActions?: (row: Row<TData>) => React.ReactNode;
  renderExpandedRow?: (row: Row<TData>) => React.ReactNode;
  renderEmptyState?: () => React.ReactNode;

  // Initial states
  initialSorting?: SortingState;
  initialFilters?: ColumnFiltersState;
  initialExpanded?: ExpandedState;
  initialGrouping?: GroupingState;
  initialColumnVisibility?: VisibilityState;
}

// Memoized fuzzy filter function
const fuzzyFilter = <TData,>(
  row: Row<TData>,
  columnId: string,
  value: string,
  addMeta: (meta: { itemRank: ReturnType<typeof rankItem> }) => void
) => {
  const itemRank = rankItem(row.getValue(columnId), value);
  addMeta({ itemRank });
  return itemRank.passed;
};

// Memoized table row component
const TableRowComponent = React.memo(
  <TData,>({
    row,
    striped,
    highlightOnHover,
    colorPalette,
    onRowClick,
    onRowDoubleClick,
    renderRowActions,
    renderExpandedRow,
  }: {
    row: Row<TData>;
    striped?: boolean;
    highlightOnHover?: boolean;
    colorPalette?: string;
    onRowClick?: (row: Row<TData>) => void;
    onRowDoubleClick?: (row: Row<TData>) => void;
    renderRowActions?: (row: Row<TData>) => React.ReactNode;
    renderExpandedRow?: (row: Row<TData>) => React.ReactNode;
  }) => {
    const handleRowClick = useCallback(() => {
      onRowClick?.(row);
    }, [row, onRowClick]);

    const handleRowDoubleClick = useCallback(() => {
      onRowDoubleClick?.(row);
    }, [row, onRowDoubleClick]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onRowClick?.(row);
        }
      },
      [row, onRowClick]
    );

    return (
      <>
        <ChakraTableRow
          data-state={row.getIsSelected() && 'selected'}
          onClick={handleRowClick}
          onDoubleClick={handleRowDoubleClick}
          onKeyDown={handleKeyDown}
          _hover={highlightOnHover ? { bg: `${colorPalette}.50` } : undefined}
          backgroundColor={striped && row.index % 2 ? `${colorPalette}.50` : undefined}
          cursor={onRowClick ? 'pointer' : 'default'}
          tabIndex={onRowClick ? 0 : undefined}
          aria-selected={row.getIsSelected()}
        >
          {row.getVisibleCells().map((cell) => (
            <TableCell key={cell.id}>
              {cell.getIsGrouped() ? (
                <HStack gap={2}>
                  <IconButton
                    aria-label="Expand group"
                    size="xs"
                    variant="ghost"
                    onClick={row.getToggleExpandedHandler()}
                    icon={row.getIsExpanded() ? <LuChevronDown /> : <LuChevronRight />}
                  />
                  <Text fontWeight="medium">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())} (
                    {row.subRows.length})
                  </Text>
                </HStack>
              ) : cell.getIsAggregated() ? (
                flexRender(
                  cell.column.columnDef.aggregatedCell ?? cell.column.columnDef.cell,
                  cell.getContext()
                )
              ) : cell.getIsPlaceholder() ? null : (
                flexRender(cell.column.columnDef.cell, cell.getContext())
              )}
            </TableCell>
          ))}
          {renderRowActions && <TableCell>{renderRowActions(row)}</TableCell>}
        </ChakraTableRow>
        {row.getIsExpanded() && renderExpandedRow && (
          <ChakraTableRow>
            <TableCell>{renderExpandedRow(row)}</TableCell>
          </ChakraTableRow>
        )}
      </>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison - only re-render if specific props change
    return (
      prevProps.row.getIsSelected() === nextProps.row.getIsSelected() &&
      prevProps.row.getIsExpanded() === nextProps.row.getIsExpanded() &&
      JSON.stringify(prevProps.row.original) === JSON.stringify(nextProps.row.original)
    );
  }
);

TableRowComponent.displayName = 'TableRow';

// Create properly typed TableRow component
const TableRow = TableRowComponent;

// Main DataTable component
function DataTableComponent<TData, TValue>({
  columns,
  data,
  searchPlaceholder = 'Search all columns...',
  isLoading = false,
  pageSize = 10,
  pageSizeOptions = [10, 20, 30, 50, 100],

  // Features
  enableRowSelection = false,
  enableMultiSelect = true,
  enableSorting = true,
  enableFiltering = true,
  enableGlobalFilter = true,
  enableRowExpansion = false,
  enableGrouping = false,
  enableColumnVisibility = true,
  enableExport = false,
  enableStickyHeader = false,
  enablePagination = true,

  // Styling
  striped = false,
  highlightOnHover = true,
  variant = 'simple',
  size = 'md',
  colorPalette = 'gray',

  // Callbacks
  onRowSelectionChange,
  onRowClick,
  onRowDoubleClick,
  onExport,

  // Custom components
  renderTopToolbar,
  renderBottomToolbar,
  renderRowActions,
  renderExpandedRow,
  renderEmptyState,

  // Initial states
  initialSorting = [],
  initialFilters = [],
  initialExpanded = {},
  initialGrouping = [],
  initialColumnVisibility = {},
}: DataTableProps<TData, TValue>) {
  // State
  const [sorting, setSorting] = React.useState<SortingState>(initialSorting);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(initialFilters);
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>(initialColumnVisibility);
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
  const [expanded, setExpanded] = React.useState<ExpandedState>(initialExpanded);
  const [grouping, setGrouping] = React.useState<GroupingState>(initialGrouping);
  const [globalFilter, setGlobalFilter] = React.useState('');

  // Debounce global filter for performance
  const debouncedGlobalFilter = useDebouncedValue(globalFilter, 300);

  // Safe setters for unmount protection
  const mountedRef = React.useRef(false);
  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const makeSafeSetter = useCallback(<T,>(setter: React.Dispatch<React.SetStateAction<T>>) => {
    return (updater: React.SetStateAction<T>) => {
      if (!mountedRef.current) return;
      setter(updater);
    };
  }, []);

  const safeSetSorting = useMemo(() => makeSafeSetter(setSorting), [makeSafeSetter]);
  const safeSetColumnFilters = useMemo(() => makeSafeSetter(setColumnFilters), [makeSafeSetter]);
  const safeSetColumnVisibility = useMemo(
    () => makeSafeSetter(setColumnVisibility),
    [makeSafeSetter]
  );
  const safeSetRowSelection = useMemo(() => makeSafeSetter(setRowSelection), [makeSafeSetter]);
  const safeSetExpanded = useMemo(() => makeSafeSetter(setExpanded), [makeSafeSetter]);
  const safeSetGrouping = useMemo(() => makeSafeSetter(setGrouping), [makeSafeSetter]);

  // Memoize column definitions to prevent unnecessary re-renders
  const memoizedColumns = useMemo(() => columns, [columns]);

  // Memoize table options
  const tableOptions = useMemo(
    () => ({
      data,
      columns: memoizedColumns,
      state: {
        sorting,
        columnFilters,
        columnVisibility,
        rowSelection,
        expanded,
        grouping,
        globalFilter: debouncedGlobalFilter,
      },
      onSortingChange: safeSetSorting,
      onColumnFiltersChange: safeSetColumnFilters,
      onColumnVisibilityChange: safeSetColumnVisibility,
      onRowSelectionChange: safeSetRowSelection,
      onExpandedChange: safeSetExpanded,
      onGroupingChange: safeSetGrouping,
      onGlobalFilterChange: setGlobalFilter,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
      getFilteredRowModel: enableFiltering ? getFilteredRowModel() : undefined,
      getPaginationRowModel: enablePagination ? getPaginationRowModel() : undefined,
      getExpandedRowModel: enableRowExpansion ? getExpandedRowModel() : undefined,
      getGroupedRowModel: enableGrouping ? getGroupedRowModel() : undefined,
      globalFilterFn: fuzzyFilter,
      enableRowSelection,
      enableMultiRowSelection: enableMultiSelect,
      initialState: {
        pagination: {
          pageSize,
        },
      },
    }),
    [
      data,
      memoizedColumns,
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      expanded,
      grouping,
      debouncedGlobalFilter,
      safeSetSorting,
      safeSetColumnFilters,
      safeSetColumnVisibility,
      safeSetRowSelection,
      safeSetExpanded,
      safeSetGrouping,
      enableSorting,
      enableFiltering,
      enablePagination,
      enableRowExpansion,
      enableGrouping,
      enableRowSelection,
      enableMultiSelect,
      pageSize,
    ]
  );

  // Table instance
  const table = useReactTable<TData>(tableOptions);

  // Optimized handlers
  const handleExport = useOptimizedCallback(() => {
    if (onExport) {
      const exportData =
        table.getFilteredSelectedRowModel().rows.length > 0
          ? table.getFilteredSelectedRowModel().rows.map((row) => row.original as TData)
          : table.getFilteredRowModel().rows.map((row) => row.original as TData);
      onExport(exportData);
    }
  }, [onExport, table]);

  const handleClearFilters = useOptimizedCallback(() => {
    setColumnFilters([]);
    setGlobalFilter('');
  }, []);

  const hasActiveFilters = columnFilters.length > 0 || globalFilter;
  const selectedRowCount = Object.keys(rowSelection).length;

  React.useEffect(() => {
    onRowSelectionChange?.(rowSelection);
  }, [rowSelection, onRowSelectionChange]);

  // Screen reader announcements
  const [announcement, setAnnouncement] = React.useState('');

  const getColumnLabel = useCallback(
    <TData,>(col: Column<TData, unknown>) =>
      typeof col.columnDef.header === 'string' ? col.columnDef.header : col.id,
    []
  );

  React.useEffect(() => {
    if (selectedRowCount > 0) {
      setAnnouncement(`${selectedRowCount} row${selectedRowCount > 1 ? 's' : ''} selected`);
    }
  }, [selectedRowCount]);

  React.useEffect(() => {
    const sortedColumn = sorting[0];
    if (sortedColumn) {
      const column = table.getColumn(sortedColumn.id);
      if (column) {
        const direction = sortedColumn.desc ? 'descending' : 'ascending';
        setAnnouncement(`Table sorted by ${getColumnLabel(column)} ${direction}`);
      }
    }
  }, [sorting, table, getColumnLabel]);

  // Memoized pagination component - must be declared before early return
  const PaginationControls = useMemo(
    () => (
      <HStack justifyContent="space-between" alignItems="center">
        <HStack gap={2}>
          <Text fontSize="sm" color="#4B5563">
            Showing {table.getState().pagination.pageIndex * pageSize + 1} to{' '}
            {Math.min((table.getState().pagination.pageIndex + 1) * pageSize, data.length)} of{' '}
            {data.length} results
          </Text>
          {enableRowSelection && (
            <Text fontSize="sm" color="#4B5563">
              ({table.getFilteredSelectedRowModel().rows.length} selected)
            </Text>
          )}
        </HStack>

        <HStack gap={2}>
          <HStack gap={1}>
            <IconButton
              aria-label="First page"
              size={size}
              variant="outline"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
              icon={<LuChevronLeft />}
            />
            <IconButton
              aria-label="Previous page"
              size={size}
              variant="outline"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              icon={<LuChevronLeft />}
            />
            <HStack gap={1} paddingX={3}>
              <Text fontSize="sm">
                Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
              </Text>
            </HStack>
            <IconButton
              aria-label="Next page"
              size={size}
              variant="outline"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              icon={<LuChevronRight />}
            />
            <IconButton
              aria-label="Last page"
              size={size}
              variant="outline"
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
              icon={<LuChevronRight />}
            />
          </HStack>

          <select
            value={table.getState().pagination.pageSize.toString()}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
              table.setPageSize(Number(e.target.value));
            }}
            aria-label="Page size"
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size.toString()}>
                Show {size}
              </option>
            ))}
          </select>
        </HStack>
      </HStack>
    ),
    [table, pageSize, data.length, enableRowSelection, size, pageSizeOptions]
  );

  // Show skeleton when loading
  if (isLoading) {
    return (
      <DataTableSkeleton
        rows={pageSize}
        columns={columns.length}
        showCheckbox={enableRowSelection}
        showActions={!!renderRowActions}
        showToolbar={!renderTopToolbar}
        showPagination={enablePagination}
      />
    );
  }

  return (
    <Stack gap={4}>
      {/* Screen reader announcements */}
      <Box
        position="absolute"
        width="1px"
        height="1px"
        padding={0}
        margin="-1px"
        overflow="hidden"
        clip="rect(0, 0, 0, 0)"
        whiteSpace="nowrap"
        border="0px solid"
        aria-live="polite"
        aria-atomic="true"
      >
        {announcement}
      </Box>

      {/* Top Toolbar */}
      <VStack align="stretch" gap={3}>
        {renderTopToolbar ? (
          renderTopToolbar(table)
        ) : (
          <>
            {/* Search and actions */}
            <HStack justifyContent="space-between" flexWrap="wrap" gap={4}>
              <HStack gap={2} flex="1" maxWidth="md">
                {enableGlobalFilter && (
                  <Input
                    placeholder={searchPlaceholder}
                    value={globalFilter ?? ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setGlobalFilter(e.target.value)
                    }
                    aria-label="Search table"
                  />
                )}
              </HStack>

              <HStack gap={2}>
                {selectedRowCount > 0 && <Badge>{selectedRowCount} selected</Badge>}

                {hasActiveFilters && (
                  <Button size={size} variant="ghost" onClick={handleClearFilters}>
                    <LuTrash2 /> Clear filters
                  </Button>
                )}

                {enableExport && (
                  <IconButton aria-label="Export" onClick={handleExport} icon={<LuDownload />} />
                )}

                {enableColumnVisibility && (
                  <Popover.Root>
                    <Popover.Trigger asChild>
                      <IconButton
                        aria-label="View columns"
                        size="sm"
                        variant="ghost"
                        icon={<LuColumns2 />}
                      />
                    </Popover.Trigger>
                    <Popover.Positioner>
                      <Popover.Content>
                        <VStack align="stretch" padding={3} minWidth="200px">
                          <Text fontWeight="medium" fontSize="sm" marginBottom={2}>
                            Toggle columns
                          </Text>
                          {table
                            .getAllColumns()
                            .filter((column) => column.getCanHide())
                            .map((column) => {
                              return (
                                <Checkbox
                                  key={column.id}
                                  checked={column.getIsVisible()}
                                  onCheckedChange={(details) => {
                                    column.toggleVisibility(details.checked === true);
                                  }}
                                >
                                  {getColumnLabel(column)}
                                </Checkbox>
                              );
                            })}
                        </VStack>
                      </Popover.Content>
                    </Popover.Positioner>
                  </Popover.Root>
                )}
              </HStack>
            </HStack>

            {/* Active groupings display */}
            {enableGrouping && grouping.length > 0 && (
              <HStack gap={2} wrap="wrap">
                <Text fontSize="sm" color="#4B5563">
                  Grouped by:
                </Text>
                {grouping.map((columnId) => {
                  const column = table.getColumn(columnId);
                  return column ? (
                    <Badge key={columnId} colorPalette="gray">
                      {getColumnLabel(column)}
                      <IconButton
                        aria-label="Remove grouping"
                        size="xs"
                        variant="ghost"
                        onClick={() => column.toggleGrouping()}
                        icon={<LuTrash2 />}
                      />
                    </Badge>
                  ) : null;
                })}
                <Button size="sm" variant="ghost" onClick={() => table.resetGrouping()}>
                  Clear all
                </Button>
              </HStack>
            )}

            {/* Selected row actions */}
            {selectedRowCount > 0 && (
              <HStack
                padding={2}
                backgroundColor={`${colorPalette}.50`}
                borderRadius="md"
                justifyContent="space-between"
              >
                <Text fontSize="sm" fontWeight="medium">
                  {selectedRowCount} row{selectedRowCount > 1 ? 's' : ''} selected
                </Text>
                <HStack>
                  <Button size="sm" variant="ghost">
                    <LuCopy /> Copy
                  </Button>
                  <Button size="sm" variant="ghost" colorPalette="red">
                    <LuTrash2 /> Delete
                  </Button>
                </HStack>
              </HStack>
            )}
          </>
        )}
      </VStack>

      {/* Table */}
      <Box
        as="section"
        overflowX="auto"
        border="1px solid"
        borderRadius="lg"
        position="relative"
        aria-label="Data table"
        tabIndex={0}
      >
        <TableRoot variant={variant === 'striped' ? 'striped' : 'simple'} size={size}>
          <TableHeader
            position={enableStickyHeader ? 'sticky' : 'relative'}
            top={0}
            zIndex={10}
            backgroundColor="white"
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <ChakraTableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const { column } = header;
                  return (
                    <TableColumnHeader key={header.id}>
                      {header.isPlaceholder ? null : (
                        <HStack justifyContent="space-between">
                          <HStack gap={1}>
                            {enableGrouping && column.getCanGroup() && (
                              <IconButton
                                aria-label="Group by column"
                                size="xs"
                                variant={column.getIsGrouped() ? 'primary' : 'ghost'}
                                colorPalette={column.getIsGrouped() ? colorPalette : 'gray'}
                                onClick={() => column.toggleGrouping()}
                                icon={<LuGripVertical />}
                              />
                            )}
                            <Box>{flexRender(column.columnDef.header, header.getContext())}</Box>
                          </HStack>
                          {enableSorting && column.getCanSort() && (
                            <IconButton
                              aria-label="Sort"
                              size="xs"
                              variant="ghost"
                              onClick={column.getToggleSortingHandler()}
                              icon={
                                column.getIsSorted() === 'desc' ? (
                                  <LuChevronDown />
                                ) : column.getIsSorted() === 'asc' ? (
                                  <LuChevronUp />
                                ) : (
                                  <LuArrowUpDown />
                                )
                              }
                            />
                          )}
                        </HStack>
                      )}
                    </TableColumnHeader>
                  );
                })}
              </ChakraTableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length ? (
              table
                .getRowModel()
                .rows.map((row) => (
                  <TableRow
                    key={row.id}
                    row={row as Row<unknown>}
                    striped={striped}
                    highlightOnHover={highlightOnHover}
                    colorPalette={colorPalette}
                    onRowClick={onRowClick as ((row: Row<unknown>) => void) | undefined}
                    onRowDoubleClick={onRowDoubleClick as ((row: Row<unknown>) => void) | undefined}
                    renderRowActions={
                      renderRowActions as ((row: Row<unknown>) => React.ReactNode) | undefined
                    }
                    renderExpandedRow={
                      renderExpandedRow as ((row: Row<unknown>) => React.ReactNode) | undefined
                    }
                  />
                ))
            ) : (
              <ChakraTableRow>
                <TableCell textAlign="center">
                  {renderEmptyState ? (
                    renderEmptyState()
                  ) : (
                    <VStack gap={4} paddingY={8}>
                      <LuEyeOff size={48} color="#D1D5DB" />
                      <Text color="#6B7280" fontSize="lg">
                        No results found
                      </Text>
                      {hasActiveFilters && (
                        <Button
                          size="sm"
                          variant="ghost"
                          colorPalette={colorPalette}
                          onClick={handleClearFilters}
                        >
                          Clear filters
                        </Button>
                      )}
                    </VStack>
                  )}
                </TableCell>
              </ChakraTableRow>
            )}
          </TableBody>
        </TableRoot>
      </Box>

      {/* Pagination */}
      {renderBottomToolbar ? renderBottomToolbar(table) : enablePagination && PaginationControls}
    </Stack>
  );
}

// Memoized DataTable with optimized comparison
export const DataTableOptimized = memoizeComponent(
  DataTableComponent,
  createSelectivePropsComparison(['data', 'columns'])
) as typeof DataTableComponent;

// Re-export column helpers from original
export {
  createSortableColumn,
  createSelectionColumn,
  createExpanderColumn,
  createActionsColumn,
  createDateColumn,
  createCurrencyColumn,
  createStatusColumn,
  createGroupableColumn,
} from './data-table';
